import React from 'react';
import { motion } from 'framer-motion';

const BlackFridayRemorseLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Black Friday & Cyber Monday Remorse: Overcome Post-Sale Shopping Guilt
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Regretting Your Sale Purchases?</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Calculate your holiday shopping guilt score and get personalized advice to recover from Black Friday & Cyber Monday buyer's remorse.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Sale Purchases
            </motion.button>
          </div>
          <div className="text-7xl">🛍️💸</div>
        </div>
      </div>
      
      {/* Sale statistics section */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">The Post-Holiday Sale Reality</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">66%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of shoppers experience post-Black Friday regret</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$430</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average spending per person during sales</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">40%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of sale purchases go unused</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$8B+</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">spent on Cyber Monday alone</p>
          </div>
        </div>
      </section>
      
      {/* What causes Black Friday shopping guilt section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why We Feel Guilty After Sales Shopping</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⏱️</span>
              <div>
                <h3 className="font-bold dark:text-white">Time Pressure</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Limited-time deals create urgency that leads to hasty purchase decisions you later regret.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💰</span>
              <div>
                <h3 className="font-bold dark:text-white">Fear of Missing Out</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The anxiety that you'll miss incredible deals drives impulsive buying behavior.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🧠</span>
              <div>
                <h3 className="font-bold dark:text-white">Dopamine Rush</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The thrill of scoring deals triggers reward centers in your brain that fade after shopping.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💳</span>
              <div>
                <h3 className="font-bold dark:text-white">Budget Strain</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Post-holiday financial reality hits when credit card statements arrive in January.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Signs of Black Friday Remorse */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">5 Signs You're Suffering From Sale Remorse</h2>
        
        <div className="space-y-3">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0">1</div>
              <div>
                <h3 className="font-bold dark:text-white">Unopened Packages</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Purchase deliveries sitting untouched days or weeks after arrival.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0">2</div>
              <div>
                <h3 className="font-bold dark:text-white">Checking Return Policies</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Constantly reviewing how long you have to return items.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0">3</div>
              <div>
                <h3 className="font-bold dark:text-white">Credit Card Anxiety</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Feeling stressed or avoiding looking at your account balances.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0">4</div>
              <div>
                <h3 className="font-bold dark:text-white">Justification Loop</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Repeatedly explaining to yourself (or others) why you needed the items.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0">5</div>
              <div>
                <h3 className="font-bold dark:text-white">Sale Fatigue</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Feeling exhausted by emails, ads and the pressure to continue shopping post-sales.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Guilt Gauge helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Guilt Gauge Helps You Recover</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Your Sale Purchases
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Log what you bought, how much you spent, and your emotional state during Black Friday & Cyber Monday.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate Your Guilt Score
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get a personalized assessment of each purchase based on multiple factors specific to holiday sales.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Receive Actionable Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Get tailored recommendations on what to keep, what to return, and how to make peace with your decisions.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Plan Better For Next Year
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Develop a personalized strategy for future sale events to avoid repeating the same patterns.
            </p>
          </div>
        </div>
      </section>
      
      {/* Sale Shopper Types */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">What Type of Sale Shopper Are You?</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🏃</span>
              <h3 className="font-bold mt-2 dark:text-white">The Deal Hunter</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You chase the best discounts and doorbusters, often buying things you didn't plan for simply because they're marked down.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📱</span>
              <h3 className="font-bold mt-2 dark:text-white">The Tech Upgrader</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You wait all year for Black Friday and Cyber Monday to purchase the latest gadgets, whether you need them or not.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🎁</span>
              <h3 className="font-bold mt-2 dark:text-white">The Gift Overbuy</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You start buying presents with good intentions but end up with way more than your list required, including gifts for yourself.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">😰</span>
              <h3 className="font-bold mt-2 dark:text-white">The Panic Purchaser</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You feel overwhelmed by limited-time offers and buy impulsively to avoid missing out, often with little consideration of need.
            </p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Stories From Past Sale Survivors</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "Last Black Friday I spent over $900 on 'deals' and felt sick when January's credit card bill arrived. Tracking my purchases with Guilt Gauge helped me return $350 worth of items I didn't need and create a better plan for this year."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Alex T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reformed Deal Hunter</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to compete with friends to see who got the best Cyber Monday tech deals. After using Guilt Gauge to analyze my past three years of purchases, I realized 60% of my 'amazing deals' were still in their boxes."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Jordan M.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Mindful Tech Shopper</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Holiday Shopping Recovery Plan */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Your 5-Step Holiday Shopping Recovery Plan</h2>
        
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-5 space-y-4">
          <div className="flex items-start">
            <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">1</div>
            <div>
              <h3 className="font-bold dark:text-white">Audit Your Purchases</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                List everything you bought during Black Friday and Cyber Monday with their prices and your current feelings about each item.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">2</div>
            <div>
              <h3 className="font-bold dark:text-white">Check Return Windows</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Many retailers extend holiday return periods. Identify which items can still be returned and by what date.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">3</div>
            <div>
              <h3 className="font-bold dark:text-white">Create a January Budget</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Develop a realistic spending plan that accounts for holiday expenses and helps you recover financially.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">4</div>
            <div>
              <h3 className="font-bold dark:text-white">Identify Triggers</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Use Guilt Gauge to understand what emotional states or marketing tactics led to your most regrettable purchases.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">5</div>
            <div>
              <h3 className="font-bold dark:text-white">Plan Next Year's Strategy</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Create a pre-approved shopping list and budget for next holiday season to avoid impulsive decisions.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Sale Shopping Remorse</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I feel so guilty after Black Friday shopping?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Black Friday guilt stems from a combination of pressure-driven purchasing, spending more than intended, and the comedown from shopping dopamine. Our tool helps identify your specific guilt triggers.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I know if I should return something or keep it?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Use our guilt calculator to assess each purchase objectively. Good questions to ask: Will you use it at least once a month? Does it fill a genuine need? Would you buy it again at full price? If you answer "no" to all three, it's probably a return candidate.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I avoid sale regret next year?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Create a pre-approved shopping list with maximum budgets for each item. Implement a 24-hour waiting period for unplanned purchases. Use Guilt Gauge to track and analyze this year's holiday shopping to identify your personal risk factors.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is it normal to feel anxious about holiday spending?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Yes, financial anxiety after major sales is extremely common. Studies show that 67% of Americans experience financial stress after the holiday season. Track your purchases with Guilt Gauge to gain perspective and create actionable steps forward.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Process Your Sale Shopping?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Track your Black Friday and Cyber Monday purchases to understand your shopping patterns and make peace with your decisions.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Track Your Purchases Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Black Friday & Cyber Monday Shopping Remorse</h2>
          <p className="mb-3">
            Black Friday and Cyber Monday shopping remorse is the feeling of regret, anxiety, or guilt that occurs after making purchases during holiday sales events. This emotional response is often triggered by concerns about overspending, succumbing to marketing pressure, or buying items that weren't truly needed.
          </p>
          <p className="mb-3">
            Guilt Gauge helps you understand and address these feelings by tracking your emotional state during purchases, analyzing your spending patterns, and providing personalized strategies to make more satisfying shopping decisions during future sales events.
          </p>
          <p>
            Whether you're dealing with post-holiday budget strain, unwanted impulse buys, or simply trying to make sense of your Black Friday haul, our free tool helps you develop healthier habits and reduce shopping guilt once and for all.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default BlackFridayRemorseLanding;
